import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Save, X, AlertCircle } from 'lucide-react';
import { plannedEventsApi } from '../../services/cashCockpitApi';
import {
    EVENT_TYPES, EVENT_TYPE_LABELS, CONFIDENCES, RECURRENCE_PATTERNS, GROUP_COMPANIES,
    SUPPORTED_CURRENCIES,
} from '../../types/cashCockpit';
import type { PlannedCashEvent, EventType, Confidence, RecurrencePattern } from '../../types/cashCockpit';

interface Props {
    companyCode: string;
    canEdit: boolean;
    onChange?: () => void;     // called after create/update/delete so parent can refresh forecast
}

interface DraftRow {
    eventDate: string;
    eventType: EventType;
    currencyCode: string;
    amountFCY: string;         // string for controlled input
    description: string;
    confidence: Confidence;
    isRecurring: boolean;
    recurrencePattern: RecurrencePattern | '';
    recurrenceEndDate: string;
    counterpartyCompanyCode: string;
}

const emptyDraft: DraftRow = {
    eventDate: new Date().toISOString().slice(0, 10),
    eventType: 'Tax',
    currencyCode: 'EUR',
    amountFCY: '',
    description: '',
    confidence: 'High',
    isRecurring: false,
    recurrencePattern: '',
    recurrenceEndDate: '',
    counterpartyCompanyCode: '',
};

export default function PlannedEventsEditor({ companyCode, canEdit, onChange }: Props) {
    const [events, setEvents] = useState<PlannedCashEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<DraftRow | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editBuffer, setEditBuffer] = useState<DraftRow | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await plannedEventsApi.list(companyCode);
            setEvents(data);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load events');
        } finally {
            setLoading(false);
        }
    }, [companyCode]);

    useEffect(() => { void reload(); }, [reload]);

    const validateDraft = (d: DraftRow): string | null => {
        if (d.isRecurring && d.recurrenceEndDate && d.recurrenceEndDate < d.eventDate) {
            return 'Recurrence end date must be on or after the event date.';
        }
        return null;
    };

    const handleSaveNew = async () => {
        if (!draft) return;
        const amount = parseFloat(draft.amountFCY);
        if (isNaN(amount)) { setError('Amount must be a number (negative = outflow)'); return; }
        if (draft.eventType === 'Intercompany' && !draft.counterpartyCompanyCode) {
            setError('Counterparty company is required for Intercompany events'); return;
        }
        const validationError = validateDraft(draft);
        if (validationError) { setError(validationError); return; }
        try {
            await plannedEventsApi.create({
                companyCode,
                eventDate: draft.eventDate,
                eventType: draft.eventType,
                currencyCode: draft.currencyCode,
                amountFCY: amount,
                description: draft.description || null,
                confidence: draft.confidence,
                isRecurring: draft.isRecurring,
                recurrencePattern: draft.isRecurring ? (draft.recurrencePattern || null) : null,
                recurrenceEndDate: draft.isRecurring && draft.recurrenceEndDate ? draft.recurrenceEndDate : null,
                counterpartyCompanyCode: draft.eventType === 'Intercompany' ? draft.counterpartyCompanyCode : null,
            });
            setDraft(null);
            await reload();
            onChange?.();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
        }
    };

    const startEdit = (ev: PlannedCashEvent) => {
        setEditingId(ev.Id);
        setEditBuffer({
            eventDate: ev.EventDate.slice(0, 10),
            eventType: ev.EventType,
            currencyCode: ev.CurrencyCode,
            amountFCY: String(ev.AmountFCY),
            description: ev.Description ?? '',
            confidence: ev.Confidence,
            isRecurring: ev.IsRecurring,
            recurrencePattern: ev.RecurrencePattern ?? '',
            recurrenceEndDate: ev.RecurrenceEndDate ? ev.RecurrenceEndDate.slice(0, 10) : '',
            counterpartyCompanyCode: ev.CounterpartyCompanyCode ?? '',
        });
    };

    const saveEdit = async () => {
        if (editingId == null || !editBuffer) return;
        const amount = parseFloat(editBuffer.amountFCY);
        if (isNaN(amount)) { setError('Amount must be a number'); return; }
        if (editBuffer.eventType === 'Intercompany' && !editBuffer.counterpartyCompanyCode) {
            setError('Counterparty company is required for Intercompany events'); return;
        }
        const validationError = validateDraft(editBuffer);
        if (validationError) { setError(validationError); return; }
        try {
            await plannedEventsApi.update(editingId, {
                eventDate: editBuffer.eventDate,
                eventType: editBuffer.eventType,
                currencyCode: editBuffer.currencyCode,
                amountFCY: amount,
                description: editBuffer.description || null,
                confidence: editBuffer.confidence,
                isRecurring: editBuffer.isRecurring,
                recurrencePattern: editBuffer.isRecurring ? (editBuffer.recurrencePattern || null) : null,
                recurrenceEndDate: editBuffer.isRecurring && editBuffer.recurrenceEndDate ? editBuffer.recurrenceEndDate : null,
                counterpartyCompanyCode: editBuffer.eventType === 'Intercompany' ? editBuffer.counterpartyCompanyCode : null,
            });
            setEditingId(null);
            setEditBuffer(null);
            await reload();
            onChange?.();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await plannedEventsApi.delete(id);
            await reload();
            onChange?.();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    const renderRow = (ev: PlannedCashEvent) => {
        const isEditing = editingId === ev.Id;
        const buf = isEditing ? editBuffer : null;
        return (
            <tr key={ev.Id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-xs">
                    {isEditing && buf ? (
                        <input type="date" value={buf.eventDate}
                            onChange={e => setEditBuffer({ ...buf, eventDate: e.target.value })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                        />
                    ) : new Date(ev.EventDate).toLocaleDateString('de-CH')}
                </td>
                <td className="px-3 py-2 text-xs">
                    {isEditing && buf ? (
                        <select value={buf.eventType}
                            onChange={e => setEditBuffer({ ...buf, eventType: e.target.value as EventType })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                            {EVENT_TYPES.map(t => <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>)}
                        </select>
                    ) : EVENT_TYPE_LABELS[ev.EventType]}
                </td>
                <td className="px-3 py-2 text-xs font-mono tabular-nums text-right">
                    {isEditing && buf ? (
                        <input type="number" value={buf.amountFCY}
                            onChange={e => setEditBuffer({ ...buf, amountFCY: e.target.value })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-28"
                        />
                    ) : (
                        <span className={ev.AmountFCY < 0 ? 'text-red-600' : 'text-[#2C7BB6]'}>
                            {ev.AmountFCY.toLocaleString('de-CH')}
                        </span>
                    )}
                </td>
                <td className="px-3 py-2 text-xs">
                    {isEditing && buf ? (
                        <select value={buf.currencyCode}
                            onChange={e => setEditBuffer({ ...buf, currencyCode: e.target.value })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-16">
                            {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    ) : ev.CurrencyCode}
                </td>
                <td className="px-3 py-2 text-xs">
                    {isEditing && buf ? (
                        <select value={buf.confidence}
                            onChange={e => setEditBuffer({ ...buf, confidence: e.target.value as Confidence })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                            {CONFIDENCES.map(c => <option key={c}>{c}</option>)}
                        </select>
                    ) : ev.Confidence}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate">
                    {isEditing && buf ? (
                        <input type="text" value={buf.description}
                            onChange={e => setEditBuffer({ ...buf, description: e.target.value })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full"
                        />
                    ) : (ev.Description || '—')}
                </td>
                <td className="px-3 py-2 text-xs">
                    {isEditing && buf ? (
                        <div className="flex flex-col gap-1">
                            <label className="flex items-center gap-1">
                                <input type="checkbox" checked={buf.isRecurring}
                                    onChange={e => setEditBuffer({ ...buf, isRecurring: e.target.checked })}
                                />
                                {buf.isRecurring && (
                                    <select value={buf.recurrencePattern}
                                        onChange={e => setEditBuffer({ ...buf, recurrencePattern: e.target.value as RecurrencePattern })}
                                        className="border border-gray-300 rounded px-1 py-0.5 text-xs ml-1">
                                        <option value="">—</option>
                                        {RECURRENCE_PATTERNS.map(p => <option key={p}>{p}</option>)}
                                    </select>
                                )}
                            </label>
                            {buf.isRecurring && (
                                <div className="flex items-center gap-1">
                                    <span className="text-gray-500 text-[10px]">until:</span>
                                    <input type="date" value={buf.recurrenceEndDate}
                                        onChange={e => setEditBuffer({ ...buf, recurrenceEndDate: e.target.value })}
                                        className="border border-gray-300 rounded px-1 py-0.5 text-[10px]"
                                    />
                                </div>
                            )}
                            {buf.eventType === 'Intercompany' && (
                                <div className="flex items-center gap-1">
                                    <span className="text-gray-500 text-[10px]">to:</span>
                                    <select value={buf.counterpartyCompanyCode}
                                        onChange={e => setEditBuffer({ ...buf, counterpartyCompanyCode: e.target.value })}
                                        className="border border-gray-300 rounded px-1 py-0.5 text-[10px] uppercase">
                                        <option value="">—</option>
                                        {GROUP_COMPANIES.filter(c => c !== companyCode).map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-0.5">
                            <span>{ev.IsRecurring ? `${ev.RecurrencePattern || 'Yes'}${ev.RecurrenceEndDate ? ` until ${new Date(ev.RecurrenceEndDate).toLocaleDateString('de-CH')}` : ''}` : '—'}</span>
                            {ev.EventType === 'Intercompany' && ev.CounterpartyCompanyCode && (
                                <span className="text-[10px] text-blue-700 uppercase">→ {ev.CounterpartyCompanyCode}</span>
                            )}
                        </div>
                    )}
                </td>
                <td className="px-3 py-2 text-xs">
                    {canEdit && (isEditing ? (
                        <div className="flex gap-1">
                            <button onClick={saveEdit} className="text-green-600 hover:text-green-800" title="Save">
                                <Save className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditBuffer(null); }}
                                className="text-gray-500 hover:text-gray-700" title="Cancel">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-1">
                            <button onClick={() => startEdit(ev)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(ev.Id)} className="text-red-600 hover:text-red-800" title="Delete">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </td>
            </tr>
        );
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Planned Cash Events</h3>
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            Company: {companyCode?.toUpperCase() || '—'}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Negative amount = outflow. Recurring events auto-populate the forecast until the end date (optional).
                        Intercompany payments automatically flow into the counterparty's forecast as a reciprocal inflow.
                    </p>
                </div>
                {canEdit && !draft && (
                    <button
                        onClick={() => setDraft({ ...emptyDraft })}
                        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" /> New Event
                    </button>
                )}
            </div>

            {error && (
                <div className="mx-4 my-2 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
                    <AlertCircle className="w-4 h-4" /> {error}
                </div>
            )}

            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
                    <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2">Currency</th>
                        <th className="px-3 py-2">Confidence</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2">Recurring</th>
                        <th className="px-3 py-2 w-20">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {draft && (
                        <tr className="bg-blue-50 border-t border-blue-100">
                            <td className="px-3 py-2">
                                <input type="date" value={draft.eventDate}
                                    onChange={e => setDraft({ ...draft, eventDate: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <select value={draft.eventType}
                                    onChange={e => setDraft({ ...draft, eventType: e.target.value as EventType })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                                    {EVENT_TYPES.map(t => <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>)}
                                </select>
                                {(draft.eventType === 'AR' || draft.eventType === 'AP') && (
                                    <div className="text-[10px] text-gray-500 mt-1 max-w-[180px]">
                                        {draft.eventType === 'AR'
                                            ? 'Expected customer receipt. Enter a positive amount.'
                                            : 'Expected supplier payment. Enter a negative amount.'}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2 text-right">
                                <input type="number" placeholder="-10000 = outflow" value={draft.amountFCY}
                                    onChange={e => setDraft({ ...draft, amountFCY: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs text-right w-28"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <select value={draft.currencyCode}
                                    onChange={e => setDraft({ ...draft, currencyCode: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs w-16">
                                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </td>
                            <td className="px-3 py-2">
                                <select value={draft.confidence}
                                    onChange={e => setDraft({ ...draft, confidence: e.target.value as Confidence })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                                    {CONFIDENCES.map(c => <option key={c}>{c}</option>)}
                                </select>
                            </td>
                            <td className="px-3 py-2">
                                <input type="text" placeholder="e.g. VAT Q2" value={draft.description}
                                    onChange={e => setDraft({ ...draft, description: e.target.value })}
                                    className="border border-gray-300 rounded px-1 py-0.5 text-xs w-full"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <div className="flex flex-col gap-1">
                                    <label className="flex items-center gap-1 text-xs">
                                        <input type="checkbox" checked={draft.isRecurring}
                                            onChange={e => setDraft({ ...draft, isRecurring: e.target.checked })}
                                        />
                                        {draft.isRecurring && (
                                            <select value={draft.recurrencePattern}
                                                onChange={e => setDraft({ ...draft, recurrencePattern: e.target.value as RecurrencePattern })}
                                                className="border border-gray-300 rounded px-1 py-0.5 text-xs ml-1">
                                                <option value="">—</option>
                                                {RECURRENCE_PATTERNS.map(p => <option key={p}>{p}</option>)}
                                            </select>
                                        )}
                                    </label>
                                    {draft.isRecurring && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-500 text-[10px]">until:</span>
                                            <input type="date" value={draft.recurrenceEndDate}
                                                onChange={e => setDraft({ ...draft, recurrenceEndDate: e.target.value })}
                                                className="border border-gray-300 rounded px-1 py-0.5 text-[10px]"
                                                title="Optional end date — leave empty for open-ended"
                                            />
                                        </div>
                                    )}
                                    {draft.eventType === 'Intercompany' && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-500 text-[10px]">to:</span>
                                            <select value={draft.counterpartyCompanyCode}
                                                onChange={e => setDraft({ ...draft, counterpartyCompanyCode: e.target.value })}
                                                className="border border-gray-300 rounded px-1 py-0.5 text-[10px] uppercase"
                                                title="Counterparty company"
                                            >
                                                <option value="">—</option>
                                                {GROUP_COMPANIES.filter(c => c !== companyCode).map(c => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td className="px-3 py-2">
                                <div className="flex gap-1">
                                    <button onClick={handleSaveNew} className="text-green-600 hover:text-green-800" title="Save">
                                        <Save className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setDraft(null)} className="text-gray-500 hover:text-gray-700" title="Cancel">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                    {loading && !events.length && (
                        <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500 text-sm">Loading…</td></tr>
                    )}
                    {!loading && events.length === 0 && !draft && (
                        <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500 text-sm">
                            No planned events. Add one to enrich your cash forecast.
                        </td></tr>
                    )}
                    {events.map(renderRow)}
                </tbody>
            </table>
        </div>
    );
}
